import React from 'react';
import {
    ShimmeredDetailsList,
    SelectionMode,
    IColumn,
    Label,
    FontWeights,
    mergeStyleSets,
    getTheme,
    MessageBar,
    MessageBarType,
    Text,
} from '@fluentui/react';
import { LogbookEntry } from '../Model/LogbookEntry';
import { Rate, getApiService } from '../services/ApiService';
import { useAuth } from '../context/AuthContext';

const RECENT_COUNT = 5;

const fmtMoney = (v: number | null | undefined) =>
    v != null ? `${Math.round(v).toLocaleString()} Kč` : '—';

interface RecentRidesPanelProps {
    isVisible: boolean;
    onLoadingChange?: (loading: boolean) => void;
}

const RecentRidesPanel: React.FC<RecentRidesPanelProps> = ({ isVisible, onLoadingChange }) => {
    const { authResponse } = useAuth();
    const theme = getTheme();
    const [entries, setEntries] = React.useState<LogbookEntry[]>([]);
    const [rates, setRates] = React.useState<Rate[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [hasLoaded, setHasLoaded] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [isMobile, setIsMobile] = React.useState(window.innerWidth < 640);

    React.useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    const rateById = React.useMemo(() => {
        const map = new Map<string, Rate>();
        rates.forEach(r => map.set(r.id, r));
        return map;
    }, [rates]);

    const styles = mergeStyleSets({
        card: {
            background: theme.palette.white,
            borderRadius: '12px',
            padding: '24px 32px',
            boxShadow: theme.effects.elevation8,
            marginBottom: '40px',
            overflow: 'auto',
            selectors: {
                '@media (max-width: 640px)': {
                    padding: '16px 14px',
                },
            },
        },
        cardHeader: {
            fontSize: '20px',
            fontWeight: FontWeights.bold,
            color: theme.palette.themeDark,
            margin: '0 0 16px 0',
        },
        // Mobile card list
        rideCard: {
            background: theme.palette.neutralLighterAlt,
            border: `1px solid ${theme.palette.neutralQuaternaryAlt}`,
            borderRadius: '8px',
            padding: '14px 16px',
            marginBottom: '10px',
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '8px',
        },
        // Both rows use the same 3-column grid so items stay perfectly aligned
        rideCardRow: {
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            gap: '8px',
        },
        envelopeLabel: {
            fontWeight: FontWeights.semibold,
            color: theme.palette.themePrimary,
            fontSize: '13px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap' as const,
            justifySelf: 'start' as const,
        },
        // Stacked label (small) + number (big)
        priceCell: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'flex-start',
            justifySelf: 'start' as const,
        },
        priceSubLabel: {
            fontSize: '10px',
            fontWeight: 600,
            color: theme.palette.neutralSecondary,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.4px',
            lineHeight: '1.2',
        },
        priceNumber: {
            fontWeight: FontWeights.bold,
            color: theme.palette.themePrimary,
            fontSize: '16px',
            fontVariantNumeric: 'tabular-nums',
            lineHeight: '1.2',
        },
        metaText: {
            fontSize: '12px',
            color: theme.palette.neutralSecondary,
            whiteSpace: 'nowrap' as const,
            justifySelf: 'center' as const,
            textAlign: 'center' as const,
        },
        carChip: {
            fontSize: '12px',
            fontWeight: FontWeights.semibold,
            color: theme.palette.themeDark,
            padding: '2px 6px',
            borderRadius: '4px',
            background: theme.palette.themeLighterAlt,
            whiteSpace: 'nowrap' as const,
            justifySelf: 'center' as const,
        },
        statusApproved: {
            display: 'inline-block',
            fontSize: '11px',
            fontWeight: 600,
            padding: '2px 7px',
            borderRadius: '12px',
            whiteSpace: 'nowrap' as const,
            color: '#ffffff',
            background: theme.palette.green,
            border: `1px solid ${theme.palette.greenDark}`,
            justifySelf: 'end' as const,
        },
        statusPending: {
            display: 'inline-block',
            fontSize: '11px',
            fontWeight: 600,
            padding: '2px 7px',
            borderRadius: '12px',
            whiteSpace: 'nowrap' as const,
            color: theme.palette.neutralSecondary,
            background: theme.palette.neutralLighter,
            border: `1px solid ${theme.palette.neutralQuaternaryAlt}`,
            justifySelf: 'end' as const,
        },
        // Purpose badges
        purposeBadge: {
            display: 'inline-block',
            fontWeight: 600,
            fontSize: '11px',
            letterSpacing: '0.3px',
            padding: '3px 9px',
            borderRadius: '12px',
            whiteSpace: 'nowrap' as const,
        },
        purposeTaxi:     { color: '#ffffff', background: '#1a7f4b', border: '1px solid #15663c' },
        purposeService:  { color: '#ffffff', background: '#0f5bab', border: '1px solid #0c4a8a' },
        purposeContract: { color: '#ffffff', background: '#c85000', border: '1px solid #a04000' },
        purposePersonal: { color: '#ffffff', background: '#7b39a8', border: '1px solid #622e87' },
        purposeUnknown: {
            color: theme.palette.neutralDark,
            background: theme.palette.neutralLighter,
            border: `1px solid ${theme.palette.neutralQuaternaryAlt}`,
        },
        // Desktop grid styles
        grid: {
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            '.ms-DetailsHeader': {
                background: theme.palette.neutralLighterAlt,
                borderBottom: `2px solid ${theme.palette.neutralQuaternaryAlt}`,
            },
            '.ms-DetailsHeader-cell': {
                fontSize: '14px',
                fontWeight: FontWeights.bold,
                color: theme.palette.neutralDark,
                padding: '12px 8px',
            },
            '.ms-DetailsRow': {
                borderBottom: `1px solid ${theme.palette.neutralLighter}`,
                '&:hover': { background: theme.palette.neutralLighterAlt },
            },
            '.ms-DetailsRow-cell': {
                padding: '12px 8px',
                fontSize: '13px',
            },
            '.ms-DetailsList-contentWrapper': {
                overflow: 'hidden',
            },
        },
    });

    const getPurposeBadge = (item: LogbookEntry) => {
        const rateTypeMap: Record<string, { text: string; cls: string }> = {
            'Taxi':     { text: '🚕 TAXI',     cls: styles.purposeTaxi },
            'Service':  { text: '🔧 Služební', cls: styles.purposeService },
            'Contract': { text: '📄 Smluvní',  cls: styles.purposeContract },
            'Personal': { text: '👤 Soukromé', cls: styles.purposePersonal },
        };
        const rate = item.purpose ? rateById.get(item.purpose) : undefined;
        const p = rate ? rateTypeMap[rate.rateType] : undefined;
        const label = p?.text ?? (rate?.purpose || item.purpose || '—');
        return <span className={`${styles.purposeBadge} ${p?.cls ?? styles.purposeUnknown}`}>{label}</span>;
    };

    const columns: IColumn[] = [
        {
            key: 'envelope',
            name: '📩 Obálka',
            fieldName: 'envelope',
            minWidth: 80,
            maxWidth: 110,
            isResizable: true,
            onRender: (item: LogbookEntry) => (
                <Label styles={{ root: { fontWeight: FontWeights.semibold, color: theme.palette.themePrimary, fontSize: '13px' } }}>
                    {item.envelope}
                </Label>
            ),
        },
        {
            key: 'finalPrice',
            name: '💰 Tržba',
            fieldName: 'finalPrice',
            minWidth: 130,
            maxWidth: 170,
            isResizable: true,
            onRender: (item: LogbookEntry) => {
                const positive = item.finalPrice >= 0;
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <span style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', color: theme.palette.neutralSecondary }}>
                            {positive ? 'Do obálky' : 'Dostaneš'}
                        </span>
                        <Label styles={{ root: { fontWeight: FontWeights.bold, fontSize: '14px', color: positive ? theme.palette.themePrimary : theme.palette.tealDark, fontVariantNumeric: 'tabular-nums' } }}>
                            {positive ? '💰' : '💸'} {fmtMoney(Math.abs(item.finalPrice))}
                        </Label>
                    </div>
                );
            },
        },
        {
            key: 'timestamp',
            name: '📅 Datum',
            fieldName: 'timestamp',
            minWidth: 100,
            maxWidth: 120,
            isResizable: true,
            onRender: (item: LogbookEntry) => (
                <Label styles={{ root: { fontSize: '13px' } }}>
                    {item.timestamp?.toLocaleDateString('cs-CZ')}
                </Label>
            ),
        },
        {
            key: 'carId',
            name: '🚗 Auto',
            fieldName: 'carId',
            minWidth: 80,
            maxWidth: 120,
            isResizable: true,
            onRender: (item: LogbookEntry) => (
                <Label styles={{ root: { fontWeight: FontWeights.semibold, fontSize: '13px', color: theme.palette.themeDark, padding: '2px 6px', borderRadius: '4px', background: theme.palette.themeLighterAlt } }}>
                    {item.carId}
                </Label>
            ),
        },
        {
            key: 'purpose',
            name: 'Účel',
            fieldName: 'purpose',
            minWidth: 110,
            maxWidth: 140,
            isResizable: true,
            onRender: getPurposeBadge,
        },
        {
            key: 'rowChecked',
            name: 'Stav',
            fieldName: 'rowChecked',
            minWidth: 120,
            maxWidth: 150,
            isResizable: false,
            onRender: (item: LogbookEntry) => item.rowChecked ? (
                <span className={styles.statusApproved}>✅ Schváleno</span>
            ) : (
                <span className={styles.statusPending}>⏳ Čeká na kontrolu</span>
            ),
        },
    ];

    React.useEffect(() => {
        if (isVisible && authResponse?.email) {
            setLoading(true);
            onLoadingChange?.(true);
            setError(null);
            Promise.all([
                getApiService().getLogbookEntries({ driverId: authResponse.email, top: RECENT_COUNT }),
                getApiService().getRates(),
            ])
                .then(([fetchedEntries, fetchedRates]) => {
                    setEntries(fetchedEntries);
                    setRates(fetchedRates);
                })
                .catch(e => setError(e instanceof Error ? e.message : 'Chyba při načítání jízd'))
                .finally(() => {
                    setLoading(false);
                    setHasLoaded(true);
                    onLoadingChange?.(false);
                });
        }
    }, [isVisible, authResponse?.email]); // eslint-disable-line react-hooks/exhaustive-deps

    // Don't render anything until the first fetch completes — prevents white-box flicker
    if (!isVisible || !hasLoaded) return null;

    return (
        <div className={styles.card}>
            <h3 className={styles.cardHeader}>🕑 Posledních {RECENT_COUNT} jízd</h3>

            {!loading && error && (
                <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: 12 } }}>
                    {error}
                </MessageBar>
            )}

            {!loading && !error && entries.length === 0 && (
                <Text style={{ color: theme.palette.neutralSecondary }}>Zatím žádné jízdy.</Text>
            )}

            {/* Mobile: card list */}
            {isMobile && (loading || entries.length > 0) && (
                <>
                    {loading
                        ? [1, 2, 3].map(i => (
                            <div key={i} className={styles.rideCard} style={{ opacity: 0.4 }}>
                                <div className={styles.rideCardRow}>
                                    <span className={styles.envelopeLabel}>📩 — — —</span>
                                    <span className={styles.carChip}>—</span>
                                    <span />
                                </div>
                                <div className={styles.rideCardRow}>
                                    <div className={styles.priceCell}>
                                        <span className={styles.priceSubLabel}>Do obálky</span>
                                        <span className={styles.priceNumber}>— Kč</span>
                                    </div>
                                    <span />
                                    <span />
                                </div>
                            </div>
                        ))
                        : entries.map(entry => (
                            <div key={entry.id} className={styles.rideCard}>
                                {/* Row 1: envelope | car chip | purpose badge */}
                                <div className={styles.rideCardRow}>
                                    <span className={styles.envelopeLabel}>📩 {entry.envelope}</span>
                                    <span className={styles.carChip}>{entry.carId}</span>
                                    <div style={{ justifySelf: 'end' }}>{getPurposeBadge(entry)}</div>
                                </div>
                                {/* Row 2: label+amount | date | status */}
                                <div className={styles.rideCardRow}>
                                    <div className={styles.priceCell}>
                                        <span className={styles.priceSubLabel}>
                                            {entry.finalPrice >= 0 ? 'Do obálky' : 'Dostaneš'}
                                        </span>
                                        <span className={styles.priceNumber}>
                                            {fmtMoney(Math.abs(entry.finalPrice))}
                                        </span>
                                    </div>
                                    {entry.timestamp && (
                                        <span className={styles.metaText}>
                                            {entry.timestamp.toLocaleDateString('cs-CZ')}
                                        </span>
                                    )}
                                    <span className={entry.rowChecked ? styles.statusApproved : styles.statusPending}>
                                        {entry.rowChecked ? '✅ Schváleno' : '⏳ Čeká'}
                                    </span>
                                </div>
                            </div>
                        ))
                    }
                </>
            )}

            {/* Desktop: full grid */}
            {!isMobile && (loading || entries.length > 0) && (
                <ShimmeredDetailsList
                    items={entries}
                    columns={columns}
                    compact={false}
                    selectionMode={SelectionMode.none}
                    enableShimmer={loading}
                    styles={{ root: styles.grid }}
                />
            )}
        </div>
    );
};

export default RecentRidesPanel;


